import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import type { Query } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import { getSupabaseAccessToken } from "./lib/supabase";
import { readBrowserCookie } from "./lib/csrf-cookie";
import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from "@shared/session";
import { redirectRecoveryCallbackToResetPage } from "./lib/password-recovery";
import { installVitePreloadErrorHandler } from "./lib/chunk-load-recovery";
import "./index.css";

try {
  installVitePreloadErrorHandler(window, sessionStorage);
} catch {
  // sessionStorage can throw in locked-down browsers
}

// Recovery links must land on /reset-password before React reads the session.
if (redirectRecoveryCallbackToResetPage()) {
  // Navigation in progress · skip mounting until the next load.
} else {

// Conditionally load analytics script if environment variables are set
const analyticsEndpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
const analyticsWebsiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;

if (analyticsEndpoint && analyticsWebsiteId) {
  try {
    // Validate that the endpoint doesn't contain placeholder values
    if (analyticsEndpoint.includes("%") || analyticsWebsiteId.includes("%")) {
      console.warn("[Analytics] Environment variables contain placeholders, skipping analytics script");
    } else {
      const script = document.createElement("script");
      script.defer = true;
      script.src = `${analyticsEndpoint}/umami`;
      script.setAttribute("data-website-id", analyticsWebsiteId);
      script.onerror = () => {
        console.warn("[Analytics] Failed to load analytics script from:", script.src);
      };
      document.head.appendChild(script);
    }
  } catch (error) {
    console.warn("[Analytics] Error setting up analytics script:", error);
  }
}

function resolveFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof Request) return input.url;
  return input.href;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const isDev = import.meta.env.DEV;

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  const loginUrl = getLoginUrl();
  // If OAuth is not configured, redirect to email/password login page
  window.location.href = loginUrl || "/login";
};

function isAuthMeQuery(query: Query): boolean {
  const key = query.queryKey;
  if (!Array.isArray(key) || !Array.isArray(key[0])) return false;
  const path = key[0] as unknown[];
  return path[0] === "auth" && path[1] === "me";
}

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    if (isAuthMeQuery(event.query)) {
      redirectToLoginIfUnauthorized(error);
    }
    console.error("[API Query Error]", error);
  }
});

// Do not redirect on every mutation failure · upload/profile saves were kicking users out.
queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    console.error("[API Mutation Error]", event.mutation.state.error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      async headers() {
        const token = await getSupabaseAccessToken();
        const headers: Record<string, string> = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        } else {
          const csrf = readBrowserCookie(CSRF_COOKIE_NAME);
          if (csrf) {
            headers[CSRF_HEADER_NAME] = csrf;
          }
        }
        return headers;
      },
      fetch(input, init) {
        const url = resolveFetchUrl(input);
        if (isDev) {
          console.log("[tRPC] request:", url);
        }
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        }).catch((error) => {
          if (isDev) {
            console.error("[tRPC] Fetch error:", error);
          }
          throw error;
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
}
